import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';

import {
  drillFragmentData,
  getFragmentData,
  graphql,
} from '@e-socna/data-layer';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { Actors as ActorsCmp } from '../../components/Actors';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { MissionForm } from '../../forms/Mission';
import { MissionForm_Mission_Fragment } from '../../forms/Mission/fragments/mission.fragment';
import { FormVariant } from '../../forms/enums/variant.enum';
import { missionKindProfileOf } from '../../app/utils/mission-kind-profile.util';
import { MissionActorsFormStep } from '../../forms/Mission/steps/Actors';
import { MissionFormTab } from '../../forms/Mission/enums/tab.enum';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { actorListKeyOf } from '../../forms/FolderBusinessEntity/steps/Actors/utils/actor-list.util';
import type { ActorPaths } from '../../forms/FolderBusinessEntity/steps/Actors/types/paths.type';

import { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../Folder/fragments/folder-business-entity-consolidation.fragment';

import { MissionPage_Tabs_Mission_Fragment } from './Tabs';

export const MissionPage_Actors_Mission_Fragment = graphql(`
  fragment MissionPage_Actors_Mission_Fragment on Mission {
    _id

    consolidation {
      internalActors {
        ...ActorsCmp_Actor_Fragment
      }
      externalActors {
        ...ActorsCmp_Actor_Fragment
      }
      areInternalActorsFallback
      areExternalActorsFallback
    }

    ...MissionForm_Mission_Fragment
  }
`);

interface Props {
  readonly isInternal: boolean;
  readonly paths?: ActorPaths;
}

export const Actors = ({ isInternal, paths }: Readonly<Props>) => {
  const { mission: rawMission, folder } = useMissionReadyContext();

  const mission = drillFragmentData(
    [MissionPage_Tabs_Mission_Fragment, MissionPage_Actors_Mission_Fragment],
    rawMission,
  );

  const folderConsolidation = getFragmentData(
    FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment,
    folder.consolidation,
  );

  const { consolidation } = mission;
  const listKey = actorListKeyOf(isInternal);
  const relevantActors = consolidation[listKey];
  const isFallback = isInternal
    ? consolidation.areInternalActorsFallback
    : consolidation.areExternalActorsFallback;
  const relevantFallbackActors = folderConsolidation[listKey];

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = paths && currentPath !== paths.consult;
  const leaveEditionMode = useCallback(() => {
    if (paths) {
      void navigate(paths.consult);
    }
  }, [navigate, paths]);

  // A sub-mission edits its actors through its quotation case.
  const isSubMission = missionKindProfileOf(
    getFragmentData(MissionForm_Mission_Fragment, mission).kind,
  ).isWithinACollaboration;

  const access = useMissionAccess(mission._id, mission);

  return (
    <>
      {/* The actors of a sub-mission follow its Configuration: its own, or
          those of its collaboration (consolidated) in devis externe. */}
      {isSubMission ? (
        <ActorsCmp
          actionsEnabled={false}
          actors={relevantActors}
          consultThirdPartyInNewTab={false}
          isInternal={isInternal}
        />
      ) : !isInEditionMode ? (
        <ActorsCmp
          actionsEnabled={false}
          actors={isFallback ? undefined : relevantActors}
          consultThirdPartyInNewTab={false}
          fallbackActors={relevantFallbackActors}
          isInternal={isInternal}
        />
      ) : (
        <Formik enableReinitialize initialValues={mission} onSubmit={noop}>
          <MissionActorsFormStep
            fallbackActors={relevantFallbackActors}
            id={
              isInternal
                ? MissionFormTab.InternalActors
                : MissionFormTab.ExternalActors
            }
            isInternal={isInternal}
            paths={paths}
            useDirectMutation
          />
        </Formik>
      )}
      {isSubMission ? (
        <RightDrawer
          isOpen={!!isInEditionMode}
          onDrawerClose={leaveEditionMode}
        >
          <MissionForm
            folderConsolidation={folder.consolidation}
            formVariant={FormVariant.Modification}
            initialValues={mission}
            missionId={mission._id}
            onCancel={leaveEditionMode}
            onSuccess={leaveEditionMode}
            stepIdsSubset={[MissionFormTab.SubMissionActors]}
            title="Modifier les Acteurs de la Sous-Mission"
          />
        </RightDrawer>
      ) : null}
      {!!paths && (
        <SpeedDial
          actions={
            isInEditionMode
              ? [
                  {
                    id: 'leaveEditionMode',
                    icon: <EditOffIcon />,
                    name: 'Quitter Mode Édition',
                    to: paths.consult,
                  },
                ]
              : [
                  {
                    id: 'editMissionActors',
                    gate: access.update,
                    icon: <EditIcon />,
                    name: `Modifier Acteurs${isInternal ? ' Internes' : ' Externes'}`,
                    to: paths.edit,
                  },
                ]
          }
          title={`Actions possibles sur les Acteurs${isInternal ? ' Internes' : ' Externes'}`}
        />
      )}
    </>
  );
};
